import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { KNOWLEDGE_POSTS, CREATE_KNOWLEDGE_POST, UPVOTE_POST, REPLY_TO_POST } from '../graphql';
import { useAuth } from '../context/AuthContext';
import { ChevronUp, MessageCircle, Plus, Search, HelpCircle, Lightbulb, Send, ChevronDown } from 'lucide-react';

const CATEGORY_CONFIG = {
  QUESTION:          { label: 'Question',         color: 'bg-purple-100 text-purple-700',  icon: HelpCircle },
  ELECTIVE_REVIEW:   { label: 'Elective Review',  color: 'bg-blue-100 text-blue-700',      icon: Lightbulb },
  PROFESSOR_PATTERN: { label: 'Prof Pattern',     color: 'bg-yellow-100 text-yellow-700',  icon: Lightbulb },
  LAB_TIP:           { label: 'Lab Tip',          color: 'bg-green-100 text-green-700',    icon: Lightbulb },
  GENERAL_TIP:       { label: 'General Tip',      color: 'bg-gray-100 text-gray-600',      icon: Lightbulb },
};

function PostCard({ post, onUpvote, onReply }) {
  const cfg = CATEGORY_CONFIG[post.category] || CATEGORY_CONFIG.GENERAL_TIP;
  const Icon = cfg.icon;
  const [showReplies, setShowReplies] = useState(post.isQuestion);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    await onReply(post.id, replyText.trim());
    setReplyText('');
    setSubmitting(false);
  };

  return (
    <div className={`card ${post.isQuestion ? 'border-l-4 border-purple-300' : ''}`}>
      <div className="flex items-start gap-3">
        {/* Upvote */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <button onClick={() => onUpvote(post.id)}
            className={`p-1.5 rounded-lg transition-colors ${post.hasUpvoted ? 'bg-indigo-100 text-indigo-600' : 'text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'}`}>
            <ChevronUp size={18} />
          </button>
          <span className="text-sm font-bold text-gray-700">{post.upvotes}</span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`badge flex items-center gap-1 ${cfg.color}`}>
              <Icon size={11} /> {cfg.label}
            </span>
            {post.subject && <span className="badge bg-gray-100 text-gray-500">{post.subject}</span>}
            {post.professor && <span className="badge bg-orange-100 text-orange-600">Prof: {post.professor}</span>}
          </div>

          <h3 className="font-semibold text-gray-900 mb-1">{post.title}</h3>
          <p className="text-sm text-gray-600 mb-2">{post.body}</p>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold text-[10px]">
                {post.author?.name?.[0]?.toUpperCase()}
              </div>
              {post.author?.name}
              {post.author?.year && <span>· Year {post.author.year}</span>}
              {post.author?.branch && <span>· {post.author.branch}</span>}
              <span>· {new Date(post.createdAt).toLocaleDateString()}</span>
            </div>
            <button onClick={() => setShowReplies(!showReplies)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 transition-colors">
              <MessageCircle size={13} />
              {post.replies?.length || 0} {post.isQuestion ? 'answer' : 'comment'}{(post.replies?.length || 0) !== 1 ? 's' : ''}
              {showReplies ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>

          {showReplies && (
            <div className="mt-3 space-y-2">
              {post.replies?.map(r => (
                <div key={r.id} className="flex items-start gap-2 pl-3 border-l-2 border-gray-100">
                  <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-bold text-[10px] flex-shrink-0 mt-0.5">
                    {r.author?.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-700">
                      {r.author?.name}
                      {r.author?.year && <span className="text-gray-400 font-normal"> · Year {r.author.year}</span>}
                    </p>
                    <p className="text-sm text-gray-600">{r.body}</p>
                  </div>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input className="input flex-1 !py-1.5 text-sm"
                  placeholder={post.isQuestion ? "Write your answer..." : "Add a comment..."}
                  value={replyText} onChange={e => setReplyText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleReply()} />
                <button onClick={handleReply} disabled={submitting || !replyText.trim()}
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  <Send size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function KnowledgePage() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ category: '', subject: '', search: '' });
  const { data, loading, refetch } = useQuery(KNOWLEDGE_POSTS, {
    variables: { category: filters.category || null, subject: filters.subject || null }
  });
  const [createPost] = useMutation(CREATE_KNOWLEDGE_POST, { onCompleted: () => refetch() });
  const [upvote] = useMutation(UPVOTE_POST, { onCompleted: () => refetch() });
  const [reply] = useMutation(REPLY_TO_POST, { onCompleted: () => refetch() });
  const [showForm, setShowForm] = useState(false);
  const [postType, setPostType] = useState('question');
  const [form, setForm] = useState({ category: 'ELECTIVE_REVIEW', title: '', body: '', subject: '', professor: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await createPost({ variables: {
      ...form,
      professor: form.professor || null,
      subject: form.subject || null,
      isQuestion: postType === 'question',
      category: postType === 'question' ? 'QUESTION' : form.category,
    }});
    setForm({ category: 'ELECTIVE_REVIEW', title: '', body: '', subject: '', professor: '' });
    setShowForm(false);
  };

  const posts = data?.knowledgePosts || [];
  const filtered = filters.search
    ? posts.filter(p => p.title.toLowerCase().includes(filters.search.toLowerCase()) || p.body.toLowerCase().includes(filters.search.toLowerCase()))
    : posts;

  const questions = filtered.filter(p => p.isQuestion);
  const tips = filtered.filter(p => !p.isQuestion);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">The Loop 🔁</h1>
          <p className="text-gray-500 text-sm mt-1">Ask questions, share tips — seniors answer, everyone learns</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
          <Plus size={18} /> Post
        </button>
      </div>

      {showForm && (
        <div className="card mb-6 mt-4">
          <div className="flex gap-2 mb-4">
            <button onClick={() => setPostType('question')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${postType === 'question' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <HelpCircle size={15} /> Ask a Question
            </button>
            <button onClick={() => setPostType('tip')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${postType === 'tip' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <Lightbulb size={15} /> Share a Tip
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            {postType === 'tip' && (
              <select className="input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                <option value="ELECTIVE_REVIEW">Elective Review</option>
                <option value="PROFESSOR_PATTERN">Professor Pattern</option>
                <option value="LAB_TIP">Lab Tip</option>
                <option value="GENERAL_TIP">General Tip</option>
              </select>
            )}
            <input className="input" required placeholder={postType === 'question' ? "What's your question?" : "Title"}
              value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
            <textarea className="input !h-28 resize-none" required
              placeholder={postType === 'question' ? "Describe your question in detail..." : "Share what you know..."}
              value={form.body} onChange={e => setForm({...form, body: e.target.value})} />
            <div className="grid grid-cols-2 gap-3">
              <input className="input" placeholder="Subject (optional)" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} />
              {postType === 'tip' && (
                <input className="input" placeholder="Professor (optional)" value={form.professor} onChange={e => setForm({...form, professor: e.target.value})} />
              )}
            </div>
            <div className="flex gap-3">
              <button className="btn-primary" type="submit">{postType === 'question' ? 'Ask Question' : 'Share Tip'}</button>
              <button className="btn-secondary" type="button" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="flex gap-3 mb-5 mt-4 flex-wrap">
        <div className="relative flex-1 min-w-40">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input !pl-8 text-sm" placeholder="Search..." value={filters.search} onChange={e => setFilters({...filters, search: e.target.value})} />
        </div>
        <select className="input w-44 text-sm" value={filters.category} onChange={e => setFilters({...filters, category: e.target.value})}>
          <option value="">All types</option>
          <option value="QUESTION">Questions only</option>
          <option value="ELECTIVE_REVIEW">Elective Reviews</option>
          <option value="PROFESSOR_PATTERN">Prof Patterns</option>
          <option value="LAB_TIP">Lab Tips</option>
          <option value="GENERAL_TIP">General Tips</option>
        </select>
        <input className="input w-32 text-sm" placeholder="Subject..." value={filters.subject} onChange={e => setFilters({...filters, subject: e.target.value})} />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" /></div>
      ) : (
        <div className="space-y-6">
          {questions.length > 0 && (
            <div>
              <h2 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <HelpCircle size={16} className="text-purple-500" /> Questions ({questions.length})
              </h2>
              <div className="space-y-3">
                {questions.map(post => (
                  <PostCard key={post.id} post={post}
                    onUpvote={id => upvote({ variables: { postId: id } })}
                    onReply={(postId, body) => reply({ variables: { postId, body } })} />
                ))}
              </div>
            </div>
          )}

          {tips.length > 0 && (
            <div>
              <h2 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Lightbulb size={16} className="text-yellow-500" /> Tips & Reviews ({tips.length})
              </h2>
              <div className="space-y-3">
                {tips.map(post => (
                  <PostCard key={post.id} post={post}
                    onUpvote={id => upvote({ variables: { postId: id } })}
                    onReply={(postId, body) => reply({ variables: { postId, body } })} />
                ))}
              </div>
            </div>
          )}

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <MessageCircle size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">Nothing here yet</p>
              <p className="text-sm mt-1">Be the first — ask a question or share something useful!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
